/**
 * tf-i18n — Internationalization minimale per TurboFattura.
 *
 * Strategia: dizionari TS embedded + helper t() con fallback.
 * No dependency esterne (no i18next, lingui, ecc.) per mantenere il bundle small.
 *
 * Uso:
 *   import { t, setLocale } from './i18n';
 *   t('common.save');                     // "Salva" (locale 'it')
 *   t('errors.network', { fallback: 'Errore di rete' });
 *   setLocale('en');
 *   t('common.save');                     // "Save"
 *
 * Window globals:
 *   window.tfI18n.t(key, opts)
 *   window.tfI18n.setLocale('en')
 *   window.tfI18n.getLocale()
 *
 * Eventi:
 *   - 'tf-i18n-change' on window con detail: { locale }
 *
 * @since v9.16 (Sprint 9)
 */

export type Locale = 'it' | 'en';
const DEFAULT_LOCALE: Locale = 'it';
const STORAGE_KEY = 'tf-locale';

// ============================================================
// Dizionari (key path: dot-separated)
// ============================================================

const dictionaries: Record<Locale, Record<string, string>> = {
  it: {
    // Common actions
    'common.save': 'Salva',
    'common.cancel': 'Annulla',
    'common.delete': 'Elimina',
    'common.edit': 'Modifica',
    'common.close': 'Chiudi',
    'common.confirm': 'Conferma',
    'common.search': 'Cerca',
    'common.loading': 'Caricamento…',
    'common.empty': 'Nessun risultato',
    'common.retry': 'Riprova',
    'common.back': 'Indietro',
    'common.next': 'Avanti',
    'common.send': 'Invia',
    'common.download': 'Scarica',

    // Auth
    'auth.login': 'Accedi',
    'auth.logout': 'Esci',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgot_password': 'Password dimenticata?',
    'auth.signup': 'Registrati',
    'auth.required': 'Devi accedere per continuare',

    // Errors
    'errors.generic': 'Si è verificato un errore',
    'errors.network': 'Errore di rete. Controlla la connessione.',
    'errors.unauthorized': 'Non sei autorizzato',
    'errors.forbidden': 'Accesso negato',
    'errors.not_found': 'Non trovato',
    'errors.validation': 'Dati non validi',

    // Fattura
    'fattura.numero': 'Numero',
    'fattura.data': 'Data',
    'fattura.cliente': 'Cliente',
    'fattura.totale': 'Totale',
    'fattura.imponibile': 'Imponibile',
    'fattura.iva': 'IVA',
    'fattura.stato': 'Stato',
    'fattura.bozza': 'Bozza',
    'fattura.inviata': 'Inviata',
    'fattura.consegnata': 'Consegnata',
    'fattura.scartata': 'Scartata',

    // Cliente
    'cliente.ragione_sociale': 'Ragione sociale',
    'cliente.partita_iva': 'P.IVA',
    'cliente.codice_fiscale': 'Codice Fiscale',
    'cliente.indirizzo': 'Indirizzo',
    'cliente.email': 'Email',
    'cliente.telefono': 'Telefono',
    'cliente.tipo_b2b': 'Azienda',
    'cliente.tipo_b2c': 'Privato',
    'cliente.tipo_pa': 'Pubblica Amministrazione',

    // Greetings (dashboard)
    'greeting.morning': 'Buongiorno',
    'greeting.afternoon': 'Buon pomeriggio',
    'greeting.evening': 'Buonasera',
    'greeting.night': 'Buonanotte',
  },

  en: {
    // Common actions
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.search': 'Search',
    'common.loading': 'Loading…',
    'common.empty': 'No results',
    'common.retry': 'Retry',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.send': 'Send',
    'common.download': 'Download',

    // Auth
    'auth.login': 'Sign in',
    'auth.logout': 'Sign out',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgot_password': 'Forgot password?',
    'auth.signup': 'Sign up',
    'auth.required': 'You must sign in to continue',

    // Errors
    'errors.generic': 'An error occurred',
    'errors.network': 'Network error. Check your connection.',
    'errors.unauthorized': 'Unauthorized',
    'errors.forbidden': 'Access denied',
    'errors.not_found': 'Not found',
    'errors.validation': 'Invalid data',

    // Fattura
    'fattura.numero': 'Number',
    'fattura.data': 'Date',
    'fattura.cliente': 'Customer',
    'fattura.totale': 'Total',
    'fattura.imponibile': 'Taxable amount',
    'fattura.iva': 'VAT',
    'fattura.stato': 'Status',
    'fattura.bozza': 'Draft',
    'fattura.inviata': 'Sent',
    'fattura.consegnata': 'Delivered',
    'fattura.scartata': 'Rejected',

    // Cliente
    'cliente.ragione_sociale': 'Company name',
    'cliente.partita_iva': 'VAT number',
    'cliente.codice_fiscale': 'Tax code',
    'cliente.indirizzo': 'Address',
    'cliente.email': 'Email',
    'cliente.telefono': 'Phone',
    'cliente.tipo_b2b': 'Business',
    'cliente.tipo_b2c': 'Consumer',
    'cliente.tipo_pa': 'Public Administration',

    // Greetings
    'greeting.morning': 'Good morning',
    'greeting.afternoon': 'Good afternoon',
    'greeting.evening': 'Good evening',
    'greeting.night': 'Good night',
  },
};

// ============================================================
// State
// ============================================================

declare global {
  interface Window {
    tfI18n?: {
      t: typeof t;
      setLocale: typeof setLocale;
      getLocale: typeof getLocale;
      detectLocale: typeof detectLocale;
    };
  }
}

let _currentLocale: Locale = readStoredLocale() || detectLocale();

function readStoredLocale(): Locale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'it' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Detect locale dal browser. Default 'it'.
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const lang = (navigator.language || 'it').slice(0, 2).toLowerCase();
  return lang === 'en' ? 'en' : 'it';
}

/**
 * Get current locale.
 */
export function getLocale(): Locale {
  return _currentLocale;
}

/**
 * Set new locale + persist + dispatch event.
 */
export function setLocale(locale: Locale): void {
  if (locale !== 'it' && locale !== 'en') {
    console.warn(`[i18n] Unknown locale: ${locale}, fallback to ${DEFAULT_LOCALE}`);
    locale = DEFAULT_LOCALE;
  }

  _currentLocale = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {}

  // Set <html lang>
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('tf-i18n-change', { detail: { locale } }));
  }
}

interface TOptions {
  /** Fallback string se la chiave non esiste */
  fallback?: string;
  /** Variabili da sostituire in {placeholder} */
  vars?: Record<string, string | number>;
}

/**
 * Translate function. Cerca la key nel dizionario locale corrente.
 * Fallback: opts.fallback > key stessa.
 *
 * @example
 *   t('common.save')                          → 'Salva'
 *   t('greeting.morning', { vars: { name: 'Mario' } })
 *   t('non.existing', { fallback: 'Default' }) → 'Default'
 */
export function t(key: string, opts: TOptions = {}): string {
  const dict = dictionaries[_currentLocale] || dictionaries[DEFAULT_LOCALE];
  let str = dict[key];

  if (str === undefined) {
    // Fallback al locale di default
    str = dictionaries[DEFAULT_LOCALE][key];
  }

  if (str === undefined) {
    str = opts.fallback ?? key;
  }

  // Variable interpolation
  if (opts.vars) {
    for (const [k, v] of Object.entries(opts.vars)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }

  return str;
}

// ============================================================
// Bootstrap window globals
// ============================================================

if (typeof window !== 'undefined') {
  // Apply current locale to <html lang> al load
  if (document.documentElement) {
    document.documentElement.lang = _currentLocale;
  }

  window.tfI18n = {
    t,
    setLocale,
    getLocale,
    detectLocale,
  };
}
